import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { getMyLicense, ORGANIZATION_LICENSE_KEY } from "../api/organization";
import { readCached } from "../api/fetchCached";
import { cacheDelete } from "../lib/cache";
import { useAuth } from "./AuthContext";

export type License = {
  active: boolean;
  license_kind: string | null;
  expires_at: string | null;
  organization_id: number | null;
  organization_name: string | null;
  // ∈ {"no_membership", "no_license", "expired", "revoked"} when inactive
  reason: string | null;
};

type LicenseContextValue = {
  license: License | null;
  loading: boolean;
  isActive: boolean;
  refresh: () => Promise<void>;
  invalidate: () => Promise<void>;
};

const LicenseContext = createContext<LicenseContextValue | null>(null);

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, bootstrapping } = useAuth();
  const [license, setLicense] = useState<License | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (revalidate = false) => {
      // Auth hasn't settled yet (hard refresh / first paint) — don't resolve
      // to "no license" on a stale `isAuthenticated`, mirroring the same
      // guard that used to live in SubscriptionContext.
      if (bootstrapping) {
        return;
      }
      if (!isAuthenticated || !user) {
        setLicense(null);
        setLoading(false);
        return;
      }

      const key = ORGANIZATION_LICENSE_KEY(user);
      if (!revalidate) {
        const cached = readCached(key) as License | null;
        if (cached !== null) {
          setLicense(cached);
          setLoading(false);
          getMyLicense(user, { revalidate: true })
            .then(setLicense)
            .catch(() => {});
          return;
        }
      }

      setLoading(true);
      try {
        const data = (await getMyLicense(user, { revalidate })) as License;
        setLicense(data);
      } catch {
        setLicense(null);
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, user, bootstrapping],
  );

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  const invalidate = useCallback(() => {
    if (user) cacheDelete(ORGANIZATION_LICENSE_KEY(user));
    return load(true);
  }, [user, load]);

  // Platform admins bypass the license gate entirely — mirrors the backend's
  // `require_active_license` exemption for user.role === "admin".
  const isActive = Boolean(license?.active) || user?.role === "admin";

  const value = useMemo(
    () => ({ license, loading, isActive, refresh, invalidate }),
    [license, loading, isActive, refresh, invalidate],
  );

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
}

export function useLicense(): LicenseContextValue {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within LicenseProvider");
  }
  return context;
}
